import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/rootReducers";
import { Portfolio, Site } from "../../sites/sites.module";
import { PricingRequestData } from "../pricingRequestStepper.module";
import {
	getPricingCountries,
	PricingStepperDefaults,
	ResolvedSectionDefault,
	ResolvedStepperDefaults,
	resolveSectionDefault,
} from "../utils/pricingStepperDefaults.utils";
import {
	FeatureName,
	useFeatureFlags,
} from "../../../common/hooks/useFeatureFlags";

export interface PricingStepperSectionState extends ResolvedSectionDefault {
	disabled: boolean;
}

export interface PricingStepperDefaultsState
	extends Record<keyof ResolvedStepperDefaults, PricingStepperSectionState> {
	loadError: boolean;
}

type SectionFields = {
	enabled: keyof Pick<
		PricingRequestData,
		"hasNegativePrice" | "hasCurtailabilityForward"
	>;
	index: keyof Pick<
		PricingRequestData,
		"negativePriceIndex" | "curtailabilityForwardIndex"
	>;
	threshold: keyof Pick<
		PricingRequestData,
		"negativePriceThreshold" | "curtailabilityForwardThreshold"
	>;
	operator: keyof Pick<
		PricingRequestData,
		"negativePriceOperator" | "curtailabilityForwardOperator"
	>;
	consecutiveBlocks: keyof Pick<
		PricingRequestData,
		| "negativePriceNBConsecutiveBlocks"
		| "curtailabilityForwardNBConsecutiveBlocks"
	>;
};

type SectionFieldValues = {
	enabled: boolean;
	index:
		| PricingRequestData["negativePriceIndex"]
		| PricingRequestData["curtailabilityForwardIndex"]
		| undefined;
	threshold: string;
	operator: string;
	consecutiveBlocks: string;
};

const sections = [
	{
		name: "negative_price",
		feature: FeatureName.pricing_stepper_negative_price,
		fields: {
			enabled: "hasNegativePrice",
			index: "negativePriceIndex",
			threshold: "negativePriceThreshold",
			operator: "negativePriceOperator",
			consecutiveBlocks: "negativePriceNBConsecutiveBlocks",
		} satisfies SectionFields,
	},
	{
		name: "curtailability",
		feature: FeatureName.pricing_stepper_curtailability_forward,
		fields: {
			enabled: "hasCurtailabilityForward",
			index: "curtailabilityForwardIndex",
			threshold: "curtailabilityForwardThreshold",
			operator: "curtailabilityForwardOperator",
			consecutiveBlocks: "curtailabilityForwardNBConsecutiveBlocks",
		} satisfies SectionFields,
	},
] as const;

function setSectionFields(
	fields: SectionFields,
	values: SectionFieldValues,
	setFieldValue: (field: keyof PricingRequestData, value: unknown) => void
): void {
	setFieldValue(fields.enabled, values.enabled);
	setFieldValue(fields.index, values.index);
	setFieldValue(fields.threshold, values.threshold);
	setFieldValue(fields.operator, values.operator);
	setFieldValue(fields.consecutiveBlocks, values.consecutiveBlocks);
}

function resolveDefaultsForSelection(
	defaults: PricingStepperDefaults | undefined,
	enabled: boolean,
	pricingTypes: { business_key?: string }[],
	countries: string[]
): ResolvedStepperDefaults {
	const empty = {
		negative_price: { status: "empty" as const },
		curtailability: { status: "empty" as const },
	};
	if (!defaults || !enabled) return empty;

	return {
		negative_price: resolveSectionDefault(
			defaults,
			pricingTypes,
			countries,
			"negative_price"
		),
		curtailability: resolveSectionDefault(
			defaults,
			pricingTypes,
			countries,
			"curtailability"
		),
	};
}

export function usePricingStepperDefaults(
	values: PricingRequestData,
	sites: (Site | Portfolio)[],
	setFieldValue: (field: string, value: unknown) => void
): PricingStepperDefaultsState {
	const { isFeature } = useFeatureFlags();
	const defaults = useSelector(
		(state: RootState) => state.pricingRequestForm.stepperDefaults
	);
	const error = useSelector(
		(state: RootState) => state.pricingRequestForm.errors.stepperDefaults
	);
	const indexes = useSelector(
		(state: RootState) => state.pricingRequestForm.pricingIndexes
	);
	const defaultsEnabled = isFeature(
		FeatureName.pricing_stepper_defaults,
		true
	);
	const pricingTypes = values.pricingType || [];
	const countries = getPricingCountries(sites);
	const selectionKey = JSON.stringify([
		pricingTypes.map(({ business_key }) => business_key),
		countries,
	]);
	const resolved = useMemo(
		() =>
			resolveDefaultsForSelection(
				defaults,
				defaultsEnabled,
				pricingTypes,
				countries
			),
		[defaults, defaultsEnabled, selectionKey]
	);
	const loadError = !!error;
	const getSectionState = (
		name: keyof ResolvedStepperDefaults
	): PricingStepperSectionState => {
		const option = resolved[name];
		return {
			...option,
			disabled: option.status === "incompatible",
		};
	};
	const state: Omit<PricingStepperDefaultsState, "loadError"> = {
		negative_price: getSectionState("negative_price"),
		curtailability: getSectionState("curtailability"),
	};

	const applySectionDefault = (
		{ fields }: (typeof sections)[number],
		featureEnabled: boolean,
		option: ResolvedSectionDefault
	) => {
		if (!defaultsEnabled || !featureEnabled || error || !defaults) return;

		const value = option.value;
		if (!value || option.status === "incompatible") {
			setSectionFields(
				fields,
				{
					enabled: false,
					index: undefined,
					threshold: "",
					operator: "",
					consecutiveBlocks: "",
				},
				setFieldValue
			);
			return;
		}

		const matchingIndex = indexes?.find(
			(index) => index.geco_id === value.index
		);
		if (!matchingIndex) return;

		setSectionFields(
			fields,
			{
				enabled: true,
				index: matchingIndex,
				threshold: String(value.threshold),
				operator: value.operator,
				consecutiveBlocks: String(value.nb_consecutive_blocks),
			},
			setFieldValue
		);
	};

	useEffect(() => {
		for (const section of sections) {
			applySectionDefault(
				section,
				isFeature(section.feature, true),
				resolved[section.name]
			);
		}
	}, [
		resolved,
		defaults,
		error,
		indexes,
		setFieldValue,
		isFeature,
		defaultsEnabled,
	]);

	return { ...state, loadError };
}
